import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { readParams } from '@d-orca/shared/navigation';
import { LEGACY_ITEM_PARAM, OPEN_ACTIVITY_PARAM, OPEN_ISSUE_PARAM } from '@constants/activitiesParams';
import { ACTIVITY_STATUS_OPTIONS } from '../activitiesFilter';
import { DEFAULT_HIDDEN_STATUSES, DEFAULT_PAGE_FILTERS, ISSUE_STATUS_KEYS, issueStatusKey, type IPageFilters } from '../pageFilters';
import { PLAN_STATUSES } from '../WorkPlan/workPlanModel';

/** Every box any view's Status checkboxes offer, by key: the tracker's plan
 *  statuses, the ladder's rungs, an issue's statuses. */
const ALL_STATUSES: readonly string[] = [...new Set<string>([...ACTIVITY_STATUS_OPTIONS, ...PLAN_STATUSES, ...ISSUE_STATUS_KEYS])];

/**
 * The link params that are FILTERS. The page keeps its filters in its own
 * state (and localStorage), so these seed that state and then leave the URL.
 * `view` and the open-item params are not here: for those the URL is the
 * state, and they stay.
 */
const FILTER_PARAMS = ['types', 'status', 'critical'] as const;

/** The legacy `item=act:<id>` / `item=iss:<id>` as the param that replaced it. */
const LEGACY_ITEM_TARGETS: readonly (readonly [prefix: string, param: string])[] = [
  ['act:', OPEN_ACTIVITY_PARAM],
  ['iss:', OPEN_ISSUE_PARAM],
];

interface ILinkedFilters {
  /** The filter set the link arrived with, or null when none did. */
  linked: IPageFilters | null;
  /** The URL still carries link params this hook has yet to strip or rewrite —
   *  every other URL writer on the page waits for that one write (see
   *  `usePanelUrlSync`'s `paused`). */
  pending: boolean;
}

/**
 * The filter set a link arrived with — `?types=issue&status=planned` from an
 * assistant link (`@d-orca/shared/navigation`) — or null when none did.
 *
 * A link's filters are the WHOLE set, over the defaults: the page must show
 * what the link promised, so nothing the reader had saved stays on. They are
 * then stripped from the URL (`replace`), as the Scheduler strips its own
 * deep-link params: left in, a reload would re-apply them over whatever the
 * reader changed since, and every later filter change would be fighting the URL.
 */
export function useLinkedFilters(): ILinkedFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  // Keyed by the filter params' own text, so the set is stable across renders
  // and a second link to the open page is a new key.
  const picked = new URLSearchParams();
  for (const name of FILTER_PARAMS) {
    const value = searchParams.get(name);
    if (value !== null) picked.set(name, value);
  }
  const key = picked.toString();

  const linked = useMemo<IPageFilters | null>(() => {
    if (key === '') return null;
    const params = readParams('activities', new URLSearchParams(key));
    const { status } = params;
    return {
      ...DEFAULT_PAGE_FILTERS,
      ...(params.types === undefined ? {} : { types: params.types }),
      critical: params.critical === true,
      // A linked status shows that status alone — either kind's box for it:
      // every other one is hidden.
      hiddenStatuses: status
        ? ALL_STATUSES.filter((box) => box !== status && box !== issueStatusKey(status))
        : [...DEFAULT_HIDDEN_STATUSES],
    };
  }, [key]);

  // A retired `?item=` link is rewritten in the same write as the strip — two
  // writes in one commit would each put back what the other changed.
  const legacyItem = searchParams.get(LEGACY_ITEM_PARAM);
  useEffect(() => {
    if (key === '' && legacyItem === null) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const name of FILTER_PARAMS) next.delete(name);
        if (legacyItem !== null) {
          next.delete(LEGACY_ITEM_PARAM);
          // An unrecognised key names nothing the page can open, so it just goes.
          const target = LEGACY_ITEM_TARGETS.find(([prefix]) => legacyItem.startsWith(prefix));
          if (target) next.set(target[1], legacyItem.slice(target[0].length));
        }
        return next;
      },
      { replace: true },
    );
  }, [key, legacyItem, setSearchParams]);

  return { linked, pending: key !== '' || legacyItem !== null };
}
